<template>
  <div class="form" id="form" ref="form">
    <form
      @submit.prevent="validate()"
      v-show="this.authType === 'login'"
    >
      <div v-for="field in currentConfig.fields" :key="field.name">
        <div v-if="field.name === 'password'" class="password-wrapper">
          <InputField
            :name="field.name"
            :type="field.type"
            :label="$t(field.label)"
            v-model="form[field.name]"
            :validation="field.validation"
            :data-qa="field.dataQa"
            :autocomplete="field.autocomplete"
            :error="errors[field.name] || ''"
            @blur="validateField(field.name)"
          />
        </div>
        <InputField
          v-else
          :name="field.name"
          :type="field.type"
          :label="$t(field.label)"
          v-model="form[field.name]"
          :validation="field.validation"
          :data-qa="field.dataQa"
          :autocomplete="field.autocomplete"
          :error="errors[field.name] || ''"
          @blur="validateField(field.name)"
        />
      </div>

      <div class="forgot-link" v-if="this.authType === 'login'">
        <p>
          {{ $t('auth.forgot') }}
          <a
            class="underline"
            @click.prevent="forgot()"
            >{{ $t('auth.link') }}</a
          >
        </p>
      </div>

      <button
        type="submit"
        class="custom-btn primary"
        :data-qa="currentConfig.submitQa"
      >
        {{ $t(currentConfig.submitText) }}
      </button>
    </form>

    <form
      @submit.prevent="validate()"
      v-show="this.authType === 'signup'"
    >
      <!-- Name fields grouped -->
      <div class="name-inputs">
        <InputField
          v-for="field in currentConfig.fields.filter(
            f => f.wrapperClass === 'name-input'
          )"
          :key="field.name"
          :name="field.name"
          :type="field.type"
          :label="$t(field.label)"
          v-model="form[field.name]"
          :validation="field.validation"
          :autocomplete="field.autocomplete"
          :error="errors[field.name] || ''"
          @blur="validateField(field.name)"
        />
      </div>

      <!-- Other fields (non-name, non-promo fields) -->
      <InputField
        v-for="field in currentConfig.fields.filter(f => !f.wrapperClass && f.name !== 'promo')"
        :key="field.name"
        :name="field.name"
        :type="field.type"
        :label="$t(field.label)"
        v-model="form[field.name]"
        :validation="field.validation"
        :autocomplete="field.autocomplete"
        :error="errors[field.name] || ''"
        @blur="validateField(field.name)"
      />

      <!-- Promo code toggle -->
      <div v-if="promoField" class="promo-toggle">
        <a v-if="!showPromo" class="underline" @click.prevent="openPromo">
          <i class="material-icons promo-picto">local_offer</i>
          {{ $t(promoField.label) }}
        </a>
        <InputField
          v-else
          ref="promoInput"
          :name="promoField.name"
          :type="promoField.type"
          :label="$t(promoField.label)"
          v-model="form[promoField.name]"
          :validation="promoField.validation"
          :scope="currentConfig.scope"
          :autocomplete="promoField.autocomplete"
        />
      </div>

      <!-- Checkboxes -->
      <div
        v-for="checkbox in currentConfig.checkboxes || []"
        :key="checkbox.name"
        class="checkbox-wrapper"
        v-show="!(checkbox.name === 'trial' && isNotProduction)"
      >
        <label :for="checkbox.name">
          {{
            $t(
              checkbox.name === 'trial' && form.promo && form.promo.length
                ? 'auth.trial.promo'
                : checkbox.label
            )
          }}
          <a
            v-if="checkbox.linkUrl"
            class="underline"
            :href="checkbox.linkUrl"
            >{{ $t(checkbox.linkText) }}</a
          >
        </label>
        <div class="checkbox">
          <div class="custom-checkbox">
            <input
              :name="checkbox.name"
              :id="checkbox.name"
              :class="{
                input: true,
                'is-danger': errors[checkbox.name]
              }"
              type="checkbox"
              :checked="getCheckboxValue(checkbox.name)"
              @change="handleCheckboxChange(checkbox.name, $event)"
            />
            <span class="checkmark">
              <span>check</span>
            </span>
          </div>
        </div>
        <!-- Error text hidden, checkbox border indicates error -->
        <!-- <span
          v-show="errors[checkbox.name]"
          class="help is-danger"
        >
          {{ errors[checkbox.name] }}
        </span> -->
      </div>

      <button type="submit" class="custom-btn primary">
        {{ $t(currentConfig.submitText) }}
      </button>
    </form>
  </div>
</template>

<script>
import { FORM_CONFIGS, getValidationRules } from './constants/formConfigs.js'
import InputField from './shared/InputField.vue'

export default {
  name: 'component-form',
  components: {
    InputField
  },

  data() {
    return {
      form: {
        firstname: null,
        lastname: null,
        email: null,
        password: null,
        trial: false,
        phone: null,
        promo: null
      },
      terms: false,
      showPromo: false,
      errors: {}
    }
  },

  props: {
    authType: {
      type: String,
      required: true
    }
  },

  computed: {
    currentConfig() {
      return FORM_CONFIGS[this.authType]
    },
    promoField() {
      return this.currentConfig.fields?.find(f => f.name === 'promo') || null
    },
    isNotProduction() {
      return process.env.NODE_ENV !== 'production'
    }
  },

  mounted() {
    // Ensure checkbox default values are properly set
    if (this.authType === 'signup') {
      // Initialize trial checkbox to false by default
      if (this.form.trial === null || this.form.trial === undefined) {
        this.form.trial = false
      }
    }
  },

  methods: {
    /**
     * Check form data before submit
     * @param {Object} scope - Form scope to check
     */
    validate() {
      this.errors = {}
      const rules = getValidationRules(this)

      for (const field of this.currentConfig.fields) {
        if (!field.validation) continue
        for (const ruleName of field.validation.split('|')) {
          const rule = rules[ruleName]
          if (!rule) continue
          if (!rule.validate(this.form[field.name])) {
            this.$set(this.errors, field.name, rule.getMessage(field.name, []))
            break
          }
        }
      }

      if (this.authType === 'signup' && !this.terms) {
        this.$set(this.errors, 'terms', this.$t('auth.validation.required'))
      }

      if (Object.keys(this.errors).length === 0) {
        this.submit()
      }
    },

    /**
     * Emit submit event to parent
     * @private
     */
    submit() {
      // Include terms checkbox in form data for signup
      const formData = { ...this.form, terms: this.terms }
      this.$emit('submit-clicked', formData)
    },

    forgot() {
      this.errors = {}
      const emailRule = getValidationRules(this).email
      if (!emailRule.validate(this.form.email)) {
        this.$set(this.errors, 'email', emailRule.getMessage('email', []))
        return
      }
      this.$emit('forgot-clicked', this.form.email)
    },

    validateField(name) {
      const field = this.currentConfig.fields.find(f => f.name === name)
      if (!field || !field.validation) return
      const rules = getValidationRules(this)
      for (const ruleName of field.validation.split('|')) {
        const rule = rules[ruleName]
        if (!rule) continue
        if (!rule.validate(this.form[name])) {
          this.$set(this.errors, name, rule.getMessage(name, []))
          return
        }
      }
      this.$delete(this.errors, name)
    },

    /**
     * Get checkbox value for display
     */
    getCheckboxValue(checkboxName) {
      return checkboxName === 'terms' ? this.terms : this.form[checkboxName]
    },

    /**
     * Handle checkbox change events
     */
    handleCheckboxChange(checkboxName, event) {
      if (checkboxName === 'terms') {
        this.terms = event.target.checked
        if (this.terms && this.errors.terms) {
          this.$delete(this.errors, 'terms')
        }
      } else {
        this.form[checkboxName] = event.target.checked
      }
    },

    openPromo() {
      this.showPromo = true
      this.$nextTick(() => {
        const input = this.$refs.promoInput?.$el?.querySelector('input')
        if (input) input.focus()
      })
    }
  }
}
</script>
